<script setup>
import { ref, computed } from 'vue';
import FieldPreview from './FieldPreview.vue';
import { BRAND_PRIMARY } from 'dashboard/constants/brandConstants';

const props = defineProps({
 fields: {
 type: Array,
 default: () => [],
 },
 selectedIndex: {
 type: Number,
 default: null,
 },
 formName: {
 type: String,
 default: '',
 },
 primaryColor: {
 type: String,
 default: BRAND_PRIMARY,
 },
});

const emit = defineEmits([
 'select',
 'remove',
 'duplicate',
 'reorder',
 'add-field',
]);

const dragOverIndex = ref(null);
const isDraggingOver = ref(false);
const dragSourceIndex = ref(null);

const regularFields = computed(() =>
 props.fields.map((f, i) => ({ ...f, _index: i })).filter(f => !f.is_tracking)
);
const trackingFields = computed(() =>
 props.fields.map((f, i) => ({ ...f, _index: i })).filter(f => f.is_tracking)
);

function onCanvasDragOver(event) {
 event.preventDefault();
 event.dataTransfer.dropEffect = 'copy';
 isDraggingOver.value = true;
}

function onCanvasDragLeave() {
 isDraggingOver.value = false;
}

function onCanvasDrop(event) {
 event.preventDefault();
 isDraggingOver.value = false;
 const fieldType = event.dataTransfer.getData('field-type');
 if (fieldType) {
 emit('add-field', fieldType);
 }
}

function onFieldDragStart(event, index) {
 dragSourceIndex.value = index;
 event.dataTransfer.setData('reorder-index', String(index));
 event.dataTransfer.effectAllowed = 'move';
}

function onFieldDragOver(event, index) {
 event.preventDefault();
 event.dataTransfer.dropEffect = 'move';
 dragOverIndex.value = index;
}

function onFieldDragLeave() {
 dragOverIndex.value = null;
}

function onFieldDrop(event, index) {
 event.preventDefault();
 event.stopPropagation();
 dragOverIndex.value = null;

 // Check if it's a reorder operation
 const sourceIndex = event.dataTransfer.getData('reorder-index');
 if (sourceIndex !== '') {
 const from = Number(sourceIndex);
 if (from === index) return;
 const newFields = [...props.fields];
 const [moved] = newFields.splice(from, 1);
 newFields.splice(index, 0, moved);
 emit('reorder', newFields);
 dragSourceIndex.value = null;
 return;
 }

 // It's a new field from palette
 const fieldType = event.dataTransfer.getData('field-type');
 if (fieldType) {
 emit('add-field', fieldType);
 }
}

function onFieldDragEnd() {
 dragSourceIndex.value = null;
 dragOverIndex.value = null;
}
</script>

<template>
 <div
 class="max-w-2xl mx-auto"
 @dragover="onCanvasDragOver"
 @dragleave="onCanvasDragLeave"
 @drop="onCanvasDrop"
 >
 <!-- Form header preview -->
 <div
 class="bg-n-solid-1 rounded-t-xl border border-b-0 border-n-weak p-6"
 >
 <div
 class="h-1 rounded-full mb-4 w-20"
 :style="{ backgroundColor: primaryColor }"
 />
 <h2
 class="text-xl font-bold text-n-slate-12 mb-1"
 >
 {{ formName || 'Novo Formulario' }}
 </h2>
 <p
 class="text-sm text-n-slate-10 !mb-0"
 >
 Preencha o formulario abaixo
 </p>
 </div>

 <!-- Fields area -->
 <div
 class="bg-n-solid-1 border border-n-weak rounded-b-xl min-h-[300px] transition-colors"
 :class="
 isDraggingOver && !fields.length
 ? 'ring-2 ring-woot-500/30 bg-woot-50/50 dark:bg-woot-500/5'
 : ''
 "
 >
 <!-- Empty state -->
 <div
 v-if="!regularFields.length && !trackingFields.length"
 class="flex flex-col items-center justify-center py-16 px-6 text-center"
 >
 <div
 class="flex items-center justify-center size-14 rounded-2xl bg-n-slate-3 mb-3"
 >
 <span
 class="i-lucide-mouse-pointer-click size-6 text-n-slate-9"
 />
 </div>
 <h3
 class="text-sm font-semibold text-n-slate-11 mb-1"
 >
 Arraste campos para ca
 </h3>
 <p
 class="text-xs text-n-slate-9 !mb-0 max-w-xs"
 >
 Clique ou arraste campos da paleta a esquerda para
 adicionar ao formulario.
 </p>
 </div>

 <!-- Field list (regular fields) -->
 <div v-else class="p-4 space-y-0">
 <div class="flex flex-wrap">
 <div
 v-for="field in regularFields"
 :key="field.id"
 :class="[
 field.width === 'half' ? 'w-1/2' : 'w-full',
 ]"
 >
 <div
 class="group/field relative rounded-lg border-2 transition-all m-1"
 :class="[
 selectedIndex === field._index
 ? 'border-woot-500 bg-woot-50/30 dark:bg-woot-500/5'
 : 'border-transparent hover:border-n-weak',
 dragOverIndex === field._index
 ? 'border-dashed border-woot-400'
 : '',
 dragSourceIndex === field._index ? 'opacity-40' : '',
 ]"
 draggable="true"
 @click.stop="$emit('select', field._index)"
 @dragstart="onFieldDragStart($event, field._index)"
 @dragover="onFieldDragOver($event, field._index)"
 @dragleave="onFieldDragLeave"
 @drop="onFieldDrop($event, field._index)"
 @dragend="onFieldDragEnd"
 >
 <!-- Field action buttons -->
 <div
 class="absolute -top-2 right-2 flex items-center gap-0.5 opacity-0 group-hover/field:opacity-100 transition-opacity z-10"
 >
 <button
 class="flex items-center justify-center size-6 rounded bg-n-solid-1 border border-n-weak text-n-slate-9 hover:text-n-slate-11 dark:hover:text-n-slate-7 shadow-sm"
 title="Mover"
 >
 <span
 class="i-lucide-grip-vertical size-3"
 />
 </button>
 <button
 class="flex items-center justify-center size-6 rounded bg-n-solid-1 border border-n-weak text-n-slate-9 hover:text-woot-600 dark:hover:text-woot-400 shadow-sm"
 title="Duplicar"
 @click.stop="$emit('duplicate', field._index)"
 >
 <span class="i-lucide-copy size-3" />
 </button>
 <button
 class="flex items-center justify-center size-6 rounded bg-n-solid-1 border border-n-weak text-n-slate-9 hover:text-red-600 dark:hover:text-red-400 shadow-sm"
 title="Remover"
 @click.stop="$emit('remove', field._index)"
 >
 <span class="i-lucide-trash-2 size-3" />
 </button>
 </div>

 <!-- Field preview -->
 <FieldPreview
 :field="field"
 :selected="selectedIndex === field._index"
 />
 </div>
 </div>
 </div>

 <!-- Tracking fields section -->
 <div v-if="trackingFields.length" class="mt-4 pt-4 border-t border-dashed border-n-weak">
 <div class="flex items-center gap-2 mb-3 px-1">
 <span class="i-lucide-radar size-4 text-emerald-500" />
 <span class="text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">
 Campos de Tracking
 </span>
 <span class="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
 Auto-preenchido
 </span>
 </div>
 <div class="grid grid-cols-2 gap-1">
 <div
 v-for="field in trackingFields"
 :key="field.id"
 class="group/field relative flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-n-weak bg-n-slate-2/50/30 cursor-pointer"
 @click.stop="$emit('select', field._index)"
 >
 <span class="i-lucide-eye-off size-3 text-n-slate-9 shrink-0" />
 <span class="text-[11px] text-n-slate-10 truncate">
 {{ field.label }}
 </span>
 <span class="text-[9px] font-mono text-n-slate-9 ml-auto shrink-0">
 {{ field.tracking_param }}
 </span>
 <button
 class="absolute -top-1.5 -right-1.5 flex items-center justify-center size-5 rounded-full bg-n-solid-1 border border-n-weak text-n-slate-9 hover:text-red-500 shadow-sm opacity-0 group-hover/field:opacity-100 transition-opacity"
 @click.stop="$emit('remove', field._index)"
 >
 <span class="i-lucide-x size-2.5" />
 </button>
 </div>
 </div>
 </div>
 </div>
 </div>

 <!-- Submit button preview -->
 <div
 v-if="fields.length"
 class="mt-4 flex justify-end"
 >
 <div
 class="px-6 py-2.5 rounded-lg text-white text-sm font-medium"
 :style="{ backgroundColor: primaryColor }"
 >
 Enviar
 </div>
 </div>
 </div>
</template>
